import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  List,
  ListItem,
  Text,
  Box,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { prompts, colid, getTypeFieldIndex } from "./env";
import {
  getAutoSaveSetting,
  setAutoSaveSetting,
  getLiveTranscriptionSetting,
} from "../utils/utils";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";

/**
 * RightSideBar Component
 *
 * Renders the right sidebar with patient list and export options.
 */
function RightSideBar({
  names,
  togglePatientList,
  isPatientListOpen,
  navigate,
  date,
  editorRef,
  date_,
  id,
  userInfo,
  validId,
  liveTranscriptionEnabled,
  setLiveTranscriptionEnabled,
  liveFactExtractionEnabled,
  setLiveFactExtractionEnabled,
  autoSaveSetting,
  setAutoSaveSetting: setAutoSaveSettingState,
  setShowPersistentComponent,
  setStateData,
  setRefId,
  setOverride,
  setUserManuallyChangedAutoSave,
  handleLiveTranscriptionChange,
  handleLiveFactExtractionChange,
  handleAutoSaveChange,
  setCondition,
  setSession,
  setCondFunc,
  patientData,
  actualPatientId,
  condition,
  isMagicNotes = false,
  filePath = null,
  viewerTimezone = "America/New_York",
}) {
  // For Vita Med Drive docs there is no real patient — use filePath as the unique thread key
  const effectivePatientId = actualPatientId || (validId !== 'unknown' ? validId : null) || id || filePath;
  // Initialize auto-save and live transcription settings
  const [autoSaveSettingState, setAutoSaveSettingStateLocal] = useState(true);
  const [liveTranscriptionSetting, setLiveTranscriptionSettingLocal] =
    useState(false);
  const [documentContent, setDocumentContent] = useState('');

  useEffect(() => {
    if (id) {
      const savedAutoSave = getAutoSaveSetting(id);
      const savedLiveTranscription = getLiveTranscriptionSetting(id);

      setAutoSaveSettingStateLocal(
        savedAutoSave !== null ? savedAutoSave : true
      );
      setLiveTranscriptionSettingLocal(
        savedLiveTranscription !== null ? savedLiveTranscription : false
      );
    }
  }, [id]);

  // Extract document content from editor for AI Assistant
  useEffect(() => {
    const extractContent = () => {
      if (editorRef?.current) {
        try {
          const editor = editorRef.current.getEditor?.(0);
          if (editor) {
            const text = editor.getText();
            setDocumentContent(text);
          }
        } catch (error) {
          console.error('Error extracting document content:', error);
        }
      }
    };

    // Extract on mount and periodically
    extractContent();
    const interval = setInterval(extractContent, 2000);

    return () => clearInterval(interval);
  }, [editorRef]);

  // Handle auto-save setting change
  const handleAutoSaveChangeLocal = (enabled) => {
    setAutoSaveSettingStateLocal(enabled);
    if (id) {
      setAutoSaveSetting(id, enabled);
    }
    if (setAutoSaveSettingState) {
      setAutoSaveSettingState(enabled);
    }
    if (handleAutoSaveChange) {
      handleAutoSaveChange(enabled);
    }
  };

  // Handle live transcription setting change
  const handleLiveTranscriptionChangeLocal = (enabled) => {
    setLiveTranscriptionSettingLocal(enabled);
    if (setLiveTranscriptionEnabled) {
      setLiveTranscriptionEnabled(enabled);
    }
    if (handleLiveTranscriptionChange) {
      handleLiveTranscriptionChange(enabled);
    }
  };

  return (
    <>
      <Drawer
        isOpen={isPatientListOpen}
        placement="right"
        onClose={togglePatientList}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Patient List On {date}</DrawerHeader>
          <DrawerBody>
            <List spacing={3}>
              {names.map((patient, index) => (
                <ListItem
                  key={`${patient[prompts[1][0]]}-${index}`}
                  className="cursor-pointer hover:bg-gray p-2 rounded-md"
                  onClick={() => {
                    console.log("Patient data being passed:", patient);
                    console.log(
                      "Type from patient:",
                      patient[prompts[getTypeFieldIndex()][0]]
                    ); // Should show the type

                    navigate(`/patient/${patient[colid]}/edit-document`, {
                      state: {
                        name: patient[prompts[0][0]],
                        id: patient[colid],
                        date_: date_,
                        [prompts[getTypeFieldIndex()][0]]:
                          patient[prompts[getTypeFieldIndex()][0]], // Add the type using prompts[getTypeFieldIndex()][0]
                        time_: patient.time_,
                        createNew: true, // Flag to create new document instead of loading existing
                      },
                    });
                  }}
                >
                  <Text fontWeight="bold">
                    {patient[prompts[0][0]]} -{" "}
                    {patient.time_
                      ? convertScheduledTime(
                          patient.date_,
                          patient.time_,
                          patient.timezone_source ?? null,
                          viewerTimezone,
                        )
                      : ""}
                  </Text>
                  {patient.last_updated && (
                    <Text fontSize="sm" color="gray.500">
                      Last Updated:{" "}
                      {new Date(patient.last_updated).toLocaleString()}
                    </Text>
                  )}
                </ListItem>
              ))}
            </List>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default RightSideBar;
